import { useCallback, useId, useLayoutEffect, useRef } from 'react';
import { Dialog } from '../../../primitives/Dialog/Dialog';
import type { BreakdownView } from '../BreakdownBody/BreakdownBody';
import {
  ConversationsPanel,
  type ConversationRowView,
  type ConversationsView,
} from '../ConversationsPanel/ConversationsPanel';
import { BreakdownPanel } from './BreakdownPanel';
import styles from './ConversationsModal.module.css';

/**
 * A list of conversations on the overlay, and everything every one of them
 * does: the dialog, the sort and page that survive a drill-down, the `View`
 * that leaves for the post, and the `Breakdown ›` that switches to the
 * breakdown and back.
 *
 * Figma draws this once — `Modal` `Type=Table` (2235:112497) — and instances it
 * per surface, changing only the title and the count beside it: Competitors'
 * `Page conversations` (2125:84169), Community's `Top voices conversations`
 * (2235:119540) and `Top communities by mention conversations` (2235:120740),
 * and the chart readout drill-down that has no frame of its own.
 *
 * **Props only.** The caller owns which conversations (it fetches the page the
 * `view` names), the `view` itself (reset on each opening from a table, kept
 * across a `Breakdown ›` round trip) and whether the breakdown is showing.
 *
 * `Breakdown ›` switches what the card shows; it does not stack a second
 * dialog (the user's call, 2026-09-26). `‹` switches back to the list where
 * it was left; `✕` closes the whole modal.
 */
export type ConversationsModalBreakdown = {
  /** True while the card shows the breakdown instead of the list. */
  showing: boolean;
  /** Figma's `Name of the conversation`. */
  name?: string;
  view?: BreakdownView;
  /** `‹` — back to the list. */
  onBack: () => void;
};

export type ConversationsModalProps = {
  open: boolean;
  onClose: () => void;
  /**
   * The current page of the list, or undefined before anything has loaded (the
   * caller keeps its last selection after `open` goes false, so the panel does
   * not empty out while it fades).
   */
  list?: { rows: ConversationRowView[]; total: number; emptyLabel?: string };
  /** `TableHeader`'s title, and what the dialog is labelled by. */
  title: string;
  /** The pill beside it — "Showing 141 conversations for @gpcarehelp". */
  badge: string;
  /** The table's accessible name, which says what this list is of. */
  label: string;
  helpText?: string;
  helpSupportingText?: string;
  view: ConversationsView;
  onViewChange: (view: ConversationsView) => void;
  /** Figma's `View` — leaves the app for the post. */
  onViewConversation?: (conversation: ConversationRowView) => void;
  /** Figma's `Breakdown ›`. */
  onBreakdown?: (conversation: ConversationRowView) => void;
  breakdown: ConversationsModalBreakdown;
};

export function ConversationsModal({
  open,
  onClose,
  list,
  title,
  badge,
  label,
  helpText,
  helpSupportingText,
  view,
  onViewChange,
  onViewConversation,
  onBreakdown,
  breakdown,
}: ConversationsModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const showingBreakdown = breakdown.showing;

  const { ref: cardRef, pin, release } = useCardResize();
  /* Freeze the card's box before the content swaps; release it once it has. */
  useLayoutEffect(() => {
    release();
  }, [showingBreakdown, release]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      describedBy={showingBreakdown ? descriptionId : undefined}
      className={styles.layer}
    >
      {/*
        **One card, two contents.** It resizes between the two — 1280 wide for
        the list, 598 for the breakdown. transitions.dev `01-card-resize`, which
        is a transition on one box. See `useCardResize`.
      */}
      <div
        ref={cardRef}
        className={`${styles.card} ${showingBreakdown ? styles.breakdown : styles.list}`}
      >
        {showingBreakdown ? (
          <BreakdownPanel
            view={breakdown.view}
            name={breakdown.name}
            onClose={onClose}
            onBack={() => {
              pin();
              breakdown.onBack();
            }}
            titleId={titleId}
            descriptionId={descriptionId}
          />
        ) : (
          list && (
            <ConversationsPanel
              rows={list.rows}
              total={list.total}
              emptyLabel={list.emptyLabel}
              title={title}
              titleId={titleId}
              badge={badge}
              label={label}
              helpText={helpText}
              helpSupportingText={helpSupportingText}
              view={view}
              onViewChange={onViewChange}
              onViewConversation={onViewConversation}
              onBreakdown={
                onBreakdown &&
                ((conversation) => {
                  pin();
                  onBreakdown(conversation);
                })
              }
            />
          )
        )}
      </div>
    </Dialog>
  );
}

/**
 * transitions.dev `01-card-resize` — tween the card's box when its content
 * swaps, rather than letting it jump from 1280 wide to 598.
 *
 * The transition itself is CSS on `.card`. What needs JavaScript is the
 * *start*: `pin()` runs **in the click handler, before React re-renders**,
 * while the outgoing content is still mounted and measurable, and freezes the
 * card at the box it has. The new content arrives, and a layout effect
 * releases the freeze — the card transitions to what `.list` / `.breakdown`
 * specify. Pinning during render does not work (measured, 2026-09-26).
 */
function useCardResize() {
  const ref = useRef<HTMLDivElement>(null);
  const pinned = useRef(false);

  const pin = useCallback(() => {
    const card = ref.current;
    if (!card) return;
    const box = card.getBoundingClientRect();
    card.style.width = `${box.width}px`;
    card.style.height = `${box.height}px`;
    pinned.current = true;
  }, []);

  const release = useCallback(() => {
    const card = ref.current;
    if (!card || !pinned.current) return;
    pinned.current = false;
    /* The reflow is what makes clearing the pin a transition rather than a jump. */
    void card.offsetWidth;
    card.style.width = '';
    card.style.height = '';
  }, []);

  return { ref, pin, release };
}
